import React, { useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Toolbar } from '@mui/material';

// 保留原有元件
import Footer from '../components/Footer';
import ContactFormSection from '../components/ContactFormSection';
import PublicHeader from '../components/PublicHeader';
import SEO from '../components/SEO';

// 全通路新元件
import { OmniHero } from '../components/omnichannel/OmniHero';
import { PlatformGrid } from '../components/omnichannel/PlatformGrid';
import { CounterSection } from '../components/omnichannel/CounterSection';
import { FeatureOmnichat } from '../components/omnichannel/FeatureOmnichat';
import { FeatureCommentReply } from '../components/omnichannel/FeatureCommentReply';
import { FeatureChatFlow } from '../components/omnichannel/FeatureChatFlow';
import { FeatureFaq } from '../components/omnichannel/FeatureFaq';
import { FeatureCallLog } from '../components/omnichannel/FeatureCallLog';
import { FeatureDailyReport } from '../components/omnichannel/FeatureDailyReport';
import { OmniIndustry } from '../components/omnichannel/OmniIndustry';
import { OmniTestimonials } from '../components/omnichannel/OmniTestimonials';

// 共用 CSS（mockup 樣式、動畫）
import '../components/omnichannel/omnichannel.css';

const LandingPage = () => {
  const location = useLocation();
  const { t } = useTranslation('landing');
  const contactFormRef = useRef(null);

  // 自動滾動到 #contact 錨點
  useEffect(() => {
    if (location.hash === '#contact') {
      setTimeout(() => {
        contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [location.hash]);

  // Scroll-reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.omni-reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToContactForm = () => {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const landingPageSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Rebaz AI",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Customer Service Software",
    "operatingSystem": "Web",
    "description": t('hero.subtitle'),
    "url": "https://rebaz.ai",
    "offers": {
      "@type": "Offer",
      "price": "1290",
      "priceCurrency": "TWD",
      "availability": "https://schema.org/InStock",
      "url": "https://rebaz.ai/pricing",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "1290",
        "priceCurrency": "TWD",
        "billingDuration": "P1M",
        "unitText": "MONTH"
      }
    },
    "featureList": [
      t('featureOmnichat.title'),
      t('featureComment.title'),
      t('featureChatFlow.title'),
      t('featureFaq.title'),
      t('featureCallLog.title'),
      t('featureReport.title'),
    ]
  };

  return (
    <>
      <SEO
        title={t('hero.title')}
        description={t('hero.subtitle')}
        keywords="AI 客服, 全通路, LINE AI, Facebook Messenger, Instagram, Web Chat, AI 語音客服, ChatFlow, FAQ, 自動回覆"
        canonicalUrl="https://rebaz.ai"
        image="https://agent-rebaz-ai.s3.ap-northeast-1.amazonaws.com/Line_AI_Hero.png"
        schema={landingPageSchema}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <PublicHeader />
        <Toolbar />

        <Box component="main" sx={{ flexGrow: 1 }}>
          {/* Hero */}
          <OmniHero onCtaClick={scrollToContactForm} />

          {/* 六大通路 */}
          <PlatformGrid />

          {/* 數據計數 */}
          <CounterSection />

          {/* Feature 1: 全通路客服 */}
          <FeatureOmnichat />

          {/* Feature 2: 社群留言回覆 */}
          <FeatureCommentReply />

          {/* Feature 3: ChatFlow 對話引導 */}
          <FeatureChatFlow />

          {/* Feature 4: FAQ 知識庫 */}
          <FeatureFaq />

          {/* Feature 5: 電話通話記錄 */}
          <FeatureCallLog />

          {/* Feature 6: AI 營運報告 */}
          <FeatureDailyReport />

          {/* 產業 */}
          <OmniIndustry />

          {/* 客戶見證 */}
          <OmniTestimonials />

          {/* 聯繫表單（保留原有樣式） */}
          <Box ref={contactFormRef} id="contact">
            <ContactFormSection
              source="homepage"
              title={t('contact.title')}
              subtitle={t('contact.subtitle')}
            />
          </Box>
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default LandingPage;
